import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, User, Lightning, SquaresFour } from "@phosphor-icons/react";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { NAV_FLOWS_BUILT, pathAllowedForUser } from "../pages/DashboardLayout";
import { useAuth } from "../lib/auth";
import { HUB_META } from "../lib/legacyRoutes";
import { api } from "../lib/api";

const clean = (label) => label.replace(/^[^\p{L}\p{N}]+/u, "").trim() || label;
const fold = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Actions rapides (verbe → page). Complétées au besoin.
const ACTIONS = [
  { label: "Ajouter un élève / contrat", to: "/app/eleves?new=1", kw: "nouveau inscrire contrat eleve" },
  { label: "Prendre les présences", to: "/app/presences", kw: "checkin presence" },
  { label: "Envoyer un lien de paiement", to: "/app/stripe?tab=liens", kw: "stripe payer facture" },
  { label: "Relancer les impayés", to: "/app/encaissements?tab=relances", kw: "retard impaye relance" },
  { label: "Envoyer une diffusion courriel", to: "/app/communications?tab=courriel", kw: "email infolettre membres" },
  { label: "Envoyer un SMS groupé", to: "/app/communications?tab=sms", kw: "texto sms" },
  { label: "Publier sur Facebook / Instagram", to: "/app/publications?tab=marketing", kw: "post reseaux" },
  { label: "Créer une affiche", to: "/app/studio-creatif?tab=affiches", kw: "poster visuel" },
  { label: "Ouvrir la salle de contrôle Jour J", to: "/app/pointage?tab=jourj", kw: "competition pointage live" },
  { label: "Lancer le livestream", to: "/app/livestream", kw: "cloudflare direct video" },
  { label: "Voir les objectifs du mois", to: "/app/pilotage-financier?tab=objectifs", kw: "kpi cible revenus" },
  { label: "Gérer les accès & rôles", to: "/app/administrateurs", kw: "admin equipe permission" },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [students, setStudents] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((o) => !o); }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Élèves : recherche serveur (≥ 2 caractères), anti-rebond 250 ms.
  useEffect(() => {
    if (!open || q.trim().length < 2) { setStudents([]); return; }
    const t = setTimeout(() => {
      api.get("/billing/contracts/search", { params: { q: q.trim() } }).then((r) => setStudents(r.data?.contracts || [])).catch(() => setStudents([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  // Pages + onglets de hubs, aplatis.
  const pages = useMemo(() => NAV_FLOWS_BUILT.flatMap((f) => f.items.filter((it) => !user || pathAllowedForUser(it.to, user)).flatMap((it) => {
    const base = { to: it.to, label: clean(it.label), flow: f.label, icon: it.icon, kw: `${it.desc || ""} ${it.to.replace("/app/", "").replace(/-/g, " ")}` };
    const hub = HUB_META[it.to];
    return hub ? [base, ...hub.tabs.map((t) => ({ to: `${it.to}?tab=${t.key}`, label: `${clean(it.label)} › ${t.label}`, flow: f.label, icon: it.icon, kw: t.legacy }))] : [base];
  })), [user]);

  const go = (to) => { setOpen(false); setQ(""); navigate(to); };
  const fq = fold(q);
  const hit = (x) => !fq || fold(x.label).includes(fq) || fold(x.kw).includes(fq);
  const pageHits = pages.filter(hit).slice(0, 12);
  const actionHits = ACTIONS.filter(hit).filter((a) => !user || pathAllowedForUser(a.to.split("?")[0], user)).slice(0, 6);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="command-palette-trigger"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-amber-500/60 hover:text-zinc-200 transition-all">
        <MagnifyingGlass size={15} />
        <span className="flex-1 text-left truncate">Rechercher… page · élève · action</span>
        <kbd className="text-[10px] bg-zinc-800 px-1.5 py-0.5 rounded border border-zinc-700 font-sans">⌘K</kbd>
      </button>

      <CommandDialog open={open} shouldFilter={false} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <CommandInput value={q} onValueChange={setQ} placeholder="Page, élève (nom/courriel) ou action…" data-testid="command-palette-input" />
        <CommandList>
          <CommandEmpty>Aucun résultat.</CommandEmpty>
          {students.length > 0 && (
            <CommandGroup heading="Élèves">
              {students.map((s) => (
                <CommandItem key={s.id} value={`eleve-${s.id}`} onSelect={() => go(`/app/eleves?q=${encodeURIComponent(s.full_name || "")}`)} data-testid={`command-student-${s.id}`}>
                  <User size={16} weight="duotone" className="mr-2 text-emerald-400" />
                  <span className="flex-1 truncate">{s.full_name}</span>
                  <span className="text-[11px] text-zinc-500 truncate">{s.discipline || ""} {s.statut ? `· ${s.statut}` : ""}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {actionHits.length > 0 && (
            <CommandGroup heading="Actions">
              {actionHits.map((a) => (
                <CommandItem key={a.to} value={`action-${a.to}`} onSelect={() => go(a.to)} data-testid={`command-action-${a.to.replace(/[^a-z-]/g, "")}`}>
                  <Lightning size={16} weight="duotone" className="mr-2 text-amber-400" />
                  <span>{a.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup heading="Pages">
            {pageHits.map((n) => {
              const Icon = n.icon || SquaresFour;
              return (
                <CommandItem key={n.to} value={`page-${n.to}`} onSelect={() => go(n.to)} data-testid={`command-item-${n.to.split("?")[0]}`}>
                  <Icon size={16} weight="regular" className="mr-2 text-amber-500" />
                  <span className="flex-1 truncate">{n.label}</span>
                  <span className="text-[11px] text-zinc-500">{n.flow}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
